import { PHOTO_LINES_MAX } from "../../scene.js";
import { SEAM, atLines, keptLines, letterCount, need, onBeat, saidLines, sayOn } from "../timing.js";
import type { Hit, Landings, PieceInput, Seam } from "../types.js";
import { AIR, SPREAD } from "./Flip.beats.js";

/**
 * Photo: the business's own photo (ADR 0058 point 11, 2026-09-29). The bar comes through the cut, runs out to both
 * edges of the frame and splits along its length into two edges that fly apart, opening the photo between them like a
 * shutter. For each line the camera pushes in on the region of the photo the line is about (read on upload, ADR 0057),
 * and the line is slammed onto the bar as the camera arrives, on the beat, or with its word when there is a voice. On
 * the way out the line sinks behind its bar, the far edge closes the photo onto the bar where it stands, and the slit
 * left between them rides the bar home, thinning, the photo in it to the cut: the photo leaves with the bar, as every
 * other piece's words do. It shut four frames before the cut and left the bar to go home alone (critic, 2026-09-29).
 *
 * Every frame here is read from the lines and the seam alone, never from the photo or its regions (the `Pick` is the
 * guarantee): the region decides where the camera goes, never when. So the sounds the worker lays for a photo piece are
 * the same whether its photo is there or not, and a photo piece without one plays the same moves on its own ground.
 *
 * One or two lines (`PHOTO_LINES_MAX`); a third is never landed.
 */

/** Frames the bar takes to run out from home to both edges of the frame; its edges split on the frame after. */
export const STRETCH = 3;
/**
 * Frames its two edges take from the split to off the frame. They set off a frame before `open` and make most of the
 * way on it, so the shutter is seen snapping open on its tick: easing in, the snap came three frames after it.
 */
export const OPEN = 4;
/** With nothing said, the frames the whole photo is seen, the camera creeping in, before it sets off for a region. */
export const WHOLE = 8;
/** Frames the camera takes to push in on a region at the most, and as the bar hands one line over to the next. */
export const PUSH = 16;
/**
 * Frames it takes at the least, on a voice: a first line said on the cut is shown 15 frames after it, which keeps the
 * voice's hold under half a second (`VOICE_LEAD_MOST`), the photo opening in music before the first word.
 */
export const PUSH_LEAST = 9;
/** Frames a line takes to sink behind its bar. */
export const SINK = 5;
/** Frames the bar takes to fly across the frame to the other side, when the next line's region needs the words there. */
const FLIGHT = 4;
/** The most frames a letter is in the air before it lands (`airOf`). */
const AIRMOST = AIR + SPREAD;
/**
 * Frames from a line starting to sink to the next line landing, end to end: sunk, a frame of bare bar, the bar's flight
 * to the other side, and the next line's letters in the air, the first set off above a bar already there. With two lines
 * the next fell while the one before was still up, and the bar flew through its letters as a red block (critic,
 * 2026-09-29). Shorter on a voice when the lines are said close together, and the bar then stays on its side.
 */
export const HANDOVER = SINK + 1 + FLIGHT + AIRMOST;
/**
 * The least frames between two landings on a voice: the line before up two frames, sunk, a frame of bare bar, and the
 * next line's letters in the air, the bar keeping its side.
 */
export const GAP = 2 + SINK + 1 + AIRMOST;
/**
 * The least frames the camera rests on a region, landed, before it sets off for the next: said close together, it
 * landed and set off again two frames later, a dip in speed and a new start rather than a landing (critic, 2026-09-29).
 * `GAP` leaves a push at its quickest after it.
 */
export const REST = GAP - PUSH_LEAST;
/**
 * Frames the far edge takes to close the photo onto the line's bar, a slit left between them; it rides the bar home
 * from `shut`, open to the cut.
 */
export const CLOSE = 6;
/**
 * Frames between the last line starting to sink and the edges starting to close: the line has sunk behind its bar
 * before the bar sets off. At three the ground came in over letters still sinking (critic, 2026-09-29).
 */
export const LEAVE = SINK;
/** Frames a voiced piece's last line stands on its bar, landed, at the least, before it starts to go. */
const SETTLE = 6;
/**
 * With nothing said, frames a line alone stands landed at the least: it is the piece's payoff, held a second as a
 * hero's or a flip's is. Two lines are each held for their reading (`need`), the spare frames shared between them.
 */
export const ALONE = 30;

/**
 * A line: its words, how long each letter is in the air before they all land together (in reading order, the first
 * furthest, as a flip slams a line), the frame it lands on the bar as the camera arrives on its region (the thud, the
 * jolt), the frame it starts to sink behind the bar (null when it stays to the end, the last piece of a run), and
 * whether the bar has the time to fly across the frame to it (`HANDOVER`), which a second line's region may ask for.
 */
export type PhotoLine = { text: string; air: number[]; lands: number; sinks: number | null; crosses: boolean };

/**
 * Every frame of a photo piece. `split` the bar leaves home and runs out to the frame's edges; `open` its edges are
 * seen apart (the shutter's tick), and are off the frame by `opened`. `pushes[k]` is the camera's move onto line k's
 * region, arriving as it lands (after the first, a whip the camera makes in the last frames of it). On the way out,
 * `leave` the last line starts to sink, `close` the far edge starts to close the photo onto the line's bar, `shut` it
 * has closed it to a slit and the bar sets off home with it; `exit` is when the piece starts to go (its last frame, for
 * the last piece of a run, which keeps its photo).
 */
export type PhotoTime = {
  split: number; open: number; opened: number;
  lines: PhotoLine[];
  pushes: Array<{ from: number; to: number }>;
  leave: number; close: number | null; shut: number | null; exit: number;
};

/**
 * When everything happens, in the piece's frames. The photo opens straight off the bar's run out, voiced or not: it
 * waited on the half beat as a still hairline across an empty ground for up to 0.4 s (critic, 2026-09-29). With nothing
 * said, the whole photo is seen for `WHOLE` frames, the first line lands on the next half beat, and the second on the
 * grid once the first has been read and handed over, the spare frames of a longer piece shared between the two. With a
 * voice, each line lands with its word (`sayOn`): the voice paces the reading.
 */
export function photoTiming(input: Pick<PieceInput, "lines">, frames: number, seam: Seam = { first: false, last: false }): PhotoTime {
  const texts = keptLines(input.lines).slice(0, PHOTO_LINES_MAX).map((i) => input.lines[i]!.trim());
  const n = texts.length;
  const shut = seam.last ? null : frames - SEAM.back;
  const close = shut === null ? null : shut - CLOSE;
  // The words start to go a frame later for every frame the piece is longer: composing sizes a voiced piece on it.
  const leave = close === null ? frames : close - LEAVE;
  const exit = close ?? frames;
  // The bar runs out once it is home: after a cut as it lands, and opening a run once it has popped in. On a voice it
  // runs out as it pops: waiting for the pop put the soonest landing on frame 18, past the most a voice may be held, and
  // a first line said on the cut landed up to 9 frames after its word (2026-09-29).
  const voiced = seam.said && n ? onVoice(saidLines(input.lines, seam).slice(0, n), SEAM.leave + STRETCH, leave, seam) : null;
  const split = voiced || !seam.first ? SEAM.leave : SEAM.pop;
  const open = split + STRETCH;
  const lands = voiced ?? onGrid(texts, open, leave, seam);
  // A line sinks as the handover to the next begins, never sooner than two frames after it landed: then the voice has
  // said the next close behind it, and the bar stays on its side.
  const sinks = lands.map((at, k) => (k < n - 1 ? Math.max(at + 2, lands[k + 1]! - HANDOVER) : seam.last ? null : leave));
  const lines = texts.map((text, k): PhotoLine => ({
    text, air: airOf(text), lands: lands[k]!, sinks: sinks[k]!, crosses: k === 0 || lands[k]! - sinks[k - 1]! >= HANDOVER,
  }));
  // The first push arrives as its line lands, as long as it may be, after the whole photo has been seen (or as it
  // opens, on a voice). Each after it sets off as the line before starts to sink, and never before the camera has
  // rested on its region (`REST`): it stays on a region while its line is read.
  const pushes = lands.map((to, k) => ({ from: k ? Math.max(sinks[k - 1]!, lands[k - 1]! + REST) : Math.max(open + (voiced ? 0 : WHOLE), to - PUSH), to }));
  return { split, open, opened: open + OPEN, lines, pushes, leave, close, shut, exit };
}

/** Frames each letter of a line is in the air, in reading order: the last `AIR`, the first `SPREAD` more, all landing at once. */
function airOf(text: string) {
  const count = letterCount(text);
  return Array.from({ length: count }, (_, j) => AIR + SPREAD * (count > 1 ? 1 - j / (count - 1) : 0));
}

/**
 * With nothing said: every landing on the music's grid (`onBeat`, an 18-frame one with no beat known). The first line
 * on the first half beat that leaves the whole photo seen and a push; a line alone held `ALONE` before the words go.
 * With two, the second on the whole beat (failing that, the half beat) that shares the spare frames most evenly, the
 * first read (`need`) and handed over (`HANDOVER`) before it and the second read after it: every spare frame went to the
 * last line, and the first of two was up a third of a second at any length (critic, 2026-09-29). Failing the grid,
 * spread over what there is, never closer than `GAP`.
 */
function onGrid(texts: string[], open: number, leave: number, seam: Seam): number[] {
  const n = texts.length;
  if (!n) return [];
  const grid = (from: number, to: number, step: number) => {
    const out: number[] = [];
    for (let k = 0; k < 400; k += step) {
      const at = onBeat(k, seam);
      if (at > to) break;
      if (at >= from && out.at(-1) !== at) out.push(at);
    }
    return out;
  };
  const first = grid(open + WHOLE + PUSH_LEAST, Infinity, 0.5)[0]!;
  const holds = texts.map((text) => (n === 1 ? Math.max(need(text), ALONE) : need(text)));
  if (n === 1 && leave - first >= holds[0]!) return [first];
  if (n === 2) {
    const spare = (at: number) => Math.min(at - HANDOVER - first - holds[0]!, leave - at - holds[1]!);
    for (const step of [1, 0.5]) {
      const at = grid(first + holds[0]! + HANDOVER, leave - holds[1]!, step);
      if (at.length) return [first, at.reduce((a, b) => (spare(b) > spare(a) ? b : a))];
    }
  }
  // Too short for the grid (never at `minFrames`): the first line as soon as it can land after the whole photo and a
  // push at its quickest, and the last read before the words go. Landing a push after the open, the first push lasted
  // a frame (proof render, 2026-09-29).
  const a = open + WHOLE + PUSH_LEAST, b = Math.max(a, leave - holds[n - 1]!);
  const lands = texts.map((_, k) => Math.round(n === 1 ? a : a + (k * (b - a)) / (n - 1)));
  for (let k = 1; k < n; k++) lands[k] = Math.max(lands[k]!, lands[k - 1]! + GAP);
  return lands;
}

/**
 * With a voice: each line lands on the half beat at or just before its word (`sayOn`), no sooner than the camera can
 * arrive (a push at its quickest after the open, or `GAP` after the line before) and no later than leaves the lines
 * after it their gap and the last its moment on the bar before it sinks. A line the voice does not say lands as soon
 * as it can. Null when a line has no such frame: the piece is too short for its voice, and keeps its own grid.
 */
function onVoice(said: Array<number | null>, open: number, leave: number, seam: Seam) {
  const n = said.length;
  const lands: number[] = [];
  for (let k = 0; k < n; k++) {
    const earliest = k ? lands[k - 1]! + GAP : open + PUSH_LEAST;
    const at = sayOn(said[k] ?? earliest, seam, { earliest, latest: leave - SETTLE - (n - 1 - k) * GAP });
    if (at === null) return null;
    lands.push(at);
  }
  return lands;
}

/**
 * Where a photo piece lands each line (`Landings`): as the camera arrives on its region and the line hits the bar. Its
 * words start to leave as the last line sinks, a frame later for every frame the piece is longer.
 */
export function photoLandings(input: Pick<PieceInput, "lines">, frames: number, seam: Seam = { first: false, last: false }): Landings {
  const t = photoTiming(input, frames, seam);
  return { lines: atLines(input.lines, t.lines.map((l) => l.lands)), leaves: seam.last ? frames : t.leave };
}

export function photoBeats(input: Pick<PieceInput, "lines">, frames: number, seam: Seam = { first: false, last: false }): Hit[] {
  const t = photoTiming(input, frames, seam);
  // The bar comes in on the cut with the next whoosh, or, opening the video, pops up at home.
  const hits: Hit[] = [seam.first ? { frame: SEAM.pop - 2, kind: "pop", step: 0 } : { frame: 0, kind: "whoosh" }];
  // The shutter: the edges snapping apart.
  hits.push({ frame: t.open, kind: "tick" });
  // Each push swells into its arrival (a whoosh peaks on its frame), and the line hitting the bar is the thud, which the
  // music dips under and compose lays a tick on.
  for (const l of t.lines) hits.push({ frame: l.lands, kind: "whoosh" }, { frame: l.lands, kind: "thud" });
  // The far edge meeting the line's bar, the photo a slit between them, as the bar sets off home with it. The run home
  // is the next piece's whoosh, which swells into the cut.
  if (t.shut !== null) hits.push({ frame: t.shut, kind: "tick" });
  return hits.filter((h) => h.frame >= 0 && h.frame < frames).sort((a, b) => a.frame - b.frame);
}
